import { inArray } from "drizzle-orm"
import type { PgColumn, PgTable } from "drizzle-orm/pg-core"
import type { DbTransaction } from "@tc/middy-decorators"

const BATCH_SIZE = 2000

/**
 * Batch a large IN query into smaller chunks to avoid PostgreSQL planner
 * choosing sequential scans on large tables (e.g., 30M+ row container table).
 *
 * When the IN list exceeds ~5K IDs, PostgreSQL overestimates result cardinality
 * and picks a seq scan over the available index. Batching into 2000-ID chunks
 * forces index usage per batch.
 */
export const batchedInQuery = async <T>(
  tx: DbTransaction,
  table: PgTable,
  column: PgColumn,
  ids: string[],
  selectFields: Record<string, PgColumn>,
): Promise<T[]> => {
  if (ids.length === 0) return []
  if (ids.length <= BATCH_SIZE) {
    return tx.select(selectFields).from(table).where(inArray(column, ids)) as Promise<T[]>
  }

  const results: T[] = []
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const batch = ids.slice(i, i + BATCH_SIZE)
    const rows = await tx
      .select(selectFields)
      .from(table)
      .where(inArray(column, batch)) as T[]
    results.push(...rows)
  }
  return results
}
